import React, { useEffect, useState } from 'react';

import { Grid } from '@material-ui/core';

import { ButtonComponent, CardComponent, PageHeader, PageHeaderNav, SettingsForm } from 'components';
import { ConfirmationDialog, SuccessActionsDialog } from 'components/Dialogs';
import { useAuthState } from 'contexts/AuthContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useSubmittingState } from 'hooks';
import { paymentsModel, userAuthenticationSettingModel } from 'models';
import { ROLES, ROUTES } from 'routes/constants';
import { PaymentsResponse } from 'types';
import { getTWithPathKey } from 'utils';
import { getUserRole } from 'utils/getUserRole';

import StripeConnectedAccountWidgets from './StripeConnectedAccountWidgets';

import { Subtext } from '../AnalyticsProviders/GoogleTagManager/style';
import { Switch } from 'components/style';
import { ErrorText, NoteText } from './style';

const PAYMENTS_ALLOWED_ROLES = [ROLES.clientOwner, ROLES.clientAdmin, ROLES.builderAdmin];

const UserPayments = () => {
  const {
    currentUser: {
      attributes: { role, clientOwner },
    },
  } = useAuthState();
  const { handleServerError } = useServerErrorActions();
  const { withPageProgressHandler } = useBusyProgressActions();
  const { isSubmitting: isAuthSubmitting, withHandlingSubmittingState: withAuthSubmittingState } = useSubmittingState();
  const { isSubmitting: isPaymentsSubmitting, withHandlingSubmittingState: withPaymentsSubmittingState } =
    useSubmittingState();

  const [isActive, setIsActive] = useState(false);
  const [initialIsActive, setInitialIsActive] = useState(false);
  const [successActionMessages, setSuccessActionMessages] = useState<string[]>([]);

  const [payments, setPayments] = useState<PaymentsResponse | null>(null);
  const [isPaymentsLoaded, setIsPaymentsLoaded] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  const getT = getTWithPathKey('pages.settings.userPayments');
  const isPaymentsAllowed = PAYMENTS_ALLOWED_ROLES.includes(getUserRole(role, clientOwner));

  useEffect(() => {
    getAuthSetting();
    if (isPaymentsAllowed) getPayments();
  }, []);

  const getAuthSetting = withPageProgressHandler(() =>
    userAuthenticationSettingModel
      .getSetting()
      .then(({ active }) => {
        setIsActive(active);
        setInitialIsActive(active);
      })
      .catch(handleServerError),
  );

  const handleSaveAuth = withAuthSubmittingState(() =>
    userAuthenticationSettingModel
      .updateSetting({ active: isActive })
      .then(({ userAuthenticationSetting, success }) => {
        setIsActive(userAuthenticationSetting.active);
        setInitialIsActive(userAuthenticationSetting.active);
        setSuccessActionMessages(success);
      })
      .catch(handleServerError),
  );

  const getPayments = withPageProgressHandler(() =>
    paymentsModel
      .getPayments()
      .then(data => {
        setPayments(data);
        setIsPaymentsLoaded(true);
      })
      .catch(handleServerError),
  );

  const handleSetupPayments = withPaymentsSubmittingState(() => {
    setShowSetupDialog(false);

    return paymentsModel.setupPayments().then(setPayments).catch(handleServerError);
  });

  const handleOpenDashboard = withPaymentsSubmittingState(() =>
    paymentsModel
      .getDashboardLink()
      .then(({ dashboardUrl }) => {
        window.open(dashboardUrl);
      })
      .catch(handleServerError),
  );

  const handleRemoveAccount = withPaymentsSubmittingState(() => {
    setShowRemoveDialog(false);

    return paymentsModel
      .removeAccount()
      .then(() => {
        setPayments(null);
        setWidgetError(null);
      })
      .catch(handleServerError);
  });

  const renderAuthSwitch = () => <Switch checked={isActive} onChange={() => setIsActive(prevState => !prevState)} />;

  const renderSetupPrompt = () => (
    <>
      <Grid item xs={12}>
        <Subtext>{getT('payments.setupDescription')}</Subtext>
      </Grid>
      <Grid item xs={12}>
        <ButtonComponent
          text={getT('payments.setupButton')}
          color="primary"
          variant="contained"
          loading={isPaymentsSubmitting}
          onClick={() => setShowSetupDialog(true)}
        />
      </Grid>
    </>
  );

  const renderConnectedAccount = () => (
    <>
      <Grid item xs={12}>
        <Subtext>{getT('payments.dashboardDescription')}</Subtext>
      </Grid>
      <Grid item xs={12}>
        <ButtonComponent
          text={getT('payments.dashboardButton')}
          color="primary"
          variant="outlined"
          loading={isPaymentsSubmitting}
          onClick={handleOpenDashboard}
        />
      </Grid>
      {widgetError && (
        <Grid item xs={12}>
          <ErrorText>{widgetError}</ErrorText>
        </Grid>
      )}
      <StripeConnectedAccountWidgets getT={key => getT(`payments.${key}`)} onError={setWidgetError} />
      <Grid item xs={12}>
        <Subtext>{getT('payments.removeDescription')}</Subtext>
      </Grid>
      <Grid item xs={12}>
        <ButtonComponent
          text={getT('payments.removeButton')}
          color="secondary"
          variant="outlined"
          loading={isPaymentsSubmitting}
          onClick={() => setShowRemoveDialog(true)}
        />
      </Grid>
    </>
  );

  return (
    <>
      <PageHeader title={getT('title')} backLink={ROUTES.settings}>
        <PageHeaderNav />
      </PageHeader>
      <SettingsForm
        isSaveButtonVisible={isActive !== initialIsActive}
        handleSave={handleSaveAuth}
        id="userAuthenticationSettings"
      >
        <CardComponent
          title={getT('authentication.cardTitle')}
          isLoading={isAuthSubmitting}
          CardHeaderNav={renderAuthSwitch}
        >
          <Grid container>
            <Grid item xs={12}>
              <Subtext>{getT('authentication.description')}</Subtext>
            </Grid>
          </Grid>
        </CardComponent>
      </SettingsForm>
      {isPaymentsAllowed && (
        <Grid container>
          <CardComponent title={getT('payments.cardTitle')} xs={12} sm={12} lg={12} isLoading={isPaymentsSubmitting}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <NoteText>{getT(initialIsActive ? 'payments.gatingNote' : 'payments.gatingNoteDisabled')}</NoteText>
              </Grid>
              {isPaymentsLoaded && (payments ? renderConnectedAccount() : renderSetupPrompt())}
            </Grid>
          </CardComponent>
        </Grid>
      )}
      <SuccessActionsDialog
        open={successActionMessages.length > 0}
        onClose={() => setSuccessActionMessages([])}
        messages={successActionMessages}
      />
      <ConfirmationDialog
        open={showSetupDialog}
        title={getT('payments.confirmationDialogTitle')}
        showWarningIcon={false}
        message={getT('payments.confirmationDialogMessage')}
        confirmText={getT('payments.confirmationDialogConfirmText')}
        handleYes={handleSetupPayments}
        handleNo={() => setShowSetupDialog(false)}
      />
      <ConfirmationDialog
        open={showRemoveDialog}
        title={getT('payments.removeDialogTitle')}
        message={getT('payments.removeDialogMessage')}
        confirmText={getT('payments.removeDialogConfirmText')}
        handleYes={handleRemoveAccount}
        handleNo={() => setShowRemoveDialog(false)}
      />
    </>
  );
};

export default UserPayments;
